/// <amd-module name="Artifacts/Root/hooks/FeedUrlResolver" />

import { ComponentState } from "Artifacts/Root/Constants";
import {
  ErrorComponentState,
  LoadingComponentState,
  ReadyComponentState,
} from "Artifacts/Root/helpers/ComponentStateHelpers";
import {
  getViewFromNameOrId,
  getViewIdentifier,
  isSameFeed,
  isSameFeedView,
  isSameView,
} from "Artifacts/Root/helpers/FeedHelpers";
import { getStore } from "Artifacts/Root/Redux";
import { RootActions } from "Artifacts/Root/redux/Actions";
import * as Res from "Artifacts/Root/Resources";
import { feedHubRoute } from "Artifacts/Root/routing/Constants";
import { IFeedParams } from "Artifacts/Root/routing/Types";
import { FeedResolvingService } from "Artifacts/Root/services/FeedResolvingService";
import { IFeed } from "Artifacts/Root/types/IFeed";
import * as React from "react";
import { useState } from "react";
import { generatePath, useHistory, useLocation, useParams } from "react-router-dom";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IServerException } from "VSS/Platform/Contribution";
import { useComponentContext } from "VSS/Platform/Layout";

export interface IUseFeed {
  feed: IFeed | undefined;
  resolution: ComponentState;
}

/**
 * hook for retrieving the current feed as determined by URL
 * @returns IFeed for use in requests to the back-end
 */
export function useFeed(allowRedirect = false): IUseFeed {
  const { pageContext } = useComponentContext();
  // Get routing objects
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  // Get redux store
  const store = getStore(pageContext);
  const { rootState } = store.getState();
  const dispatch = store.dispatch;

  const [feed, setFeed] = useState<IFeed | undefined>();
  const [resolution, setResolution] = useState<ComponentState>(LoadingComponentState());

  // synchronize component state with redux store
  if (rootState.feed !== feed) {
    setFeed(rootState.feed);
    setResolution(ReadyComponentState());
  }

  // Resolve IFeed from URL params and place in redux store
  React.useEffect(() => {
    let mounted = true;

    const feedIdentifier = (params as IFeedParams).feedIdentifier;
    if (feed == null || !feedIdentifier || (feedIdentifier && !isSameFeed(feedIdentifier, feed))) {
      // Feed has not been retrieved or does not match existing feed in state store
      const feedResolvingService: FeedResolvingService = pageContext.getService(
        "IFeedResolvingService",
      );

      announce(Utils_String.format(Res.LoadStart_Announcement, feedIdentifier));
      setResolution(LoadingComponentState());

      feedResolvingService.getFeed(feedIdentifier, allowRedirect).then(
        (resolvedFeed: IFeed | null) => {
          if (mounted) {
            if (resolvedFeed != null) {
              if (!feedIdentifier) {
                const feedViewIdentifier = resolvedFeed.selectedViewId
                  ? resolvedFeed.name + "@" + resolvedFeed.selectedViewId
                  : resolvedFeed.name;
                history.replace(generatePath(feedHubRoute, { feedIdentifier: feedViewIdentifier }));
              } else if (!isSameFeedView(feedIdentifier, resolvedFeed)) {
                const view = getViewFromNameOrId(resolvedFeed, resolvedFeed.selectedViewId);
                // update URL
                history.replace({
                  ...location,
                  pathname: location.pathname.replace(
                    // Ugly (relies on hardcoding the /feed/ url segment) but functional for now
                    new RegExp("(^\\/feed\\/){1}([^/]*)"),
                    resolvedFeed.selectedViewId && view
                      ? "$1" + resolvedFeed.name + "@" + view.name
                      : "$1" + resolvedFeed.name,
                  ),
                });
              }
              // update redux store
              dispatch(RootActions.resolveFeed(resolvedFeed));
              // update component state
              setFeed(resolvedFeed);
              announce(Utils_String.format(Res.LoadSuccess_Announcement, feedIdentifier));
              // Set resolution to ready.
              setResolution(ReadyComponentState());
            } else {
              setFeed(undefined);
              setResolution(ReadyComponentState());
            }
          }
        },
        (error: IServerException) => {
          if (mounted) {
            setFeed(undefined);
            announce(Res.LoadError_Announcement);
            setResolution(
              ErrorComponentState(Utils_String.format(Res.FeedLoadError, feedIdentifier)),
            );
          }
        },
      );
    } else if (feed != null && feedIdentifier && !isSameView(feedIdentifier, feed)) {
      if (mounted) {
        // The new feed identifier is the same base feed but different view
        const feedWithNewSelectedView = {
          ...feed,
          selectedViewId: getViewFromNameOrId(feed, getViewIdentifier(feedIdentifier))?.id,
        } as IFeed;
        // update redux store
        dispatch(RootActions.resolveFeed(feedWithNewSelectedView));
        // update component state
        setFeed(feedWithNewSelectedView);
        // Set resolution to ready.
        setResolution(ReadyComponentState());
      }
    }

    return () => {
      mounted = false;
    };
  }, [dispatch, feed, pageContext, params, rootState.feed, history, location, allowRedirect]);

  return { feed, resolution };
}
